import { useCallback, useMemo } from "react";

import { Input } from "@/components/_atoms/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/_atoms/tabs";
import { InputDate } from "@/components/_molecules/input-date/input-date";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { COLUMN_ID_CLIMATE_ACTION_FOCUS_AREA } from "@/domain/climate-action/components/climate-action-datatable/columns/climate-action-focus-area-column";
import { useGetClimateActionListForWorkspace } from "@/domain/climate-action/hooks/api/use-get-climate-action-list-for-workspace";
import { ClimateAction } from "@/domain/climate-action/models/climate-action";
import { useGetIndicatorListQuery } from "@/domain/indicator/hooks/api/use-get-indicator-list";
import { COLUMN_ID_PROJECT_FOCUS_AREA } from "@/domain/project/components/project-datatable/columns/project-focus-area-column";
import { useGetProjectListForWorkspace } from "@/domain/project/hooks/api/use-get-project-list-for-workspace";
import { Project } from "@/domain/project/models/project-model";
import { Datatable } from "@/domain/view/components/datatable/datatable";
import {
  DatatableRowDataWithCacheKey,
  defaultDatatableState,
  isRawRow,
  TSectionData,
} from "@/domain/view/components/datatable/datatable-types";
import { useWorkspaceTerminology } from "@/domain/workspace/hooks/use-workspace-terminology";
import { useCurrentWorkspaceContext } from "@/domain/workspace/hooks/useCurrentWorkspaceContext";
import { Row } from "@tanstack/react-table";

import { type EntityMode, useCreateDataRequestStore } from "../create-data-request-state";
import {
  type SelectableRow,
  toSelectedEntity,
  useClimateActionSelectionColumns,
  useIndicatorSelectionColumns,
  useProjectSelectionColumns,
} from "../use-entity-selection-columns";

const VIEW_KEY_CLIMATE_ACTION = "data-request-climate-action-selection";
const VIEW_KEY_PROJECT = "data-request-project-selection";
const VIEW_KEY_INDICATOR = "data-request-indicator-selection";
const COLUMN_ID_TITLE = "title";

function filterByTitle<TData extends SelectableRow>(
  row: Row<TSectionData<TData>>,
  columnId: string,
  value: string,
): boolean {
  if (!value || value === "") return true;
  if (!isRawRow(row.original)) return true;
  if (columnId === COLUMN_ID_TITLE) {
    return row.original.title.toLowerCase().includes(value.toLowerCase());
  }
  return false;
}

export function EntitySelectionStep() {
  const { t } = useDomainTranslation("data-request");
  const { currentWorkspace } = useCurrentWorkspaceContext();
  const terminology = useWorkspaceTerminology(currentWorkspace.id);

  const entityMode = useCreateDataRequestStore((s) => s.entityMode);
  const setEntityMode = useCreateDataRequestStore((s) => s.setEntityMode);
  const title = useCreateDataRequestStore((s) => s.title);
  const setTitle = useCreateDataRequestStore((s) => s.setTitle);
  const dueDate = useCreateDataRequestStore((s) => s.dueDate);
  const setDueDate = useCreateDataRequestStore((s) => s.setDueDate);
  const selectedEntities = useCreateDataRequestStore((s) => s.selectedEntities);
  const toggleEntity = useCreateDataRequestStore((s) => s.toggleEntity);
  const resetCount = useCreateDataRequestStore((s) => s.resetCount);

  const { climateActionList } = useGetClimateActionListForWorkspace({ workspaceId: currentWorkspace.id });
  const { projectList } = useGetProjectListForWorkspace({ workspaceId: currentWorkspace.id });
  const { indicatorList } = useGetIndicatorListQuery({ workspaceId: currentWorkspace.id });

  const safeClimateActionList = useMemo(() => climateActionList ?? [], [climateActionList]);
  const safeProjectList = useMemo(() => projectList ?? [], [projectList]);
  const safeIndicatorList = useMemo<SelectableRow[]>(() => indicatorList ?? [], [indicatorList]);

  const climateActionColumns = useClimateActionSelectionColumns({ workspace: currentWorkspace, climateActionList: safeClimateActionList });
  const projectColumns = useProjectSelectionColumns({ workspace: currentWorkspace, projectList: safeProjectList });
  const indicatorColumns = useIndicatorSelectionColumns({ indicatorList: safeIndicatorList });

  const selectedEntityIdSet = useMemo(
    () => new Set(selectedEntities.map((e) => e.entityId)),
    [selectedEntities],
  );

  const selectedClimateActionCount = useMemo(
    () => selectedEntities.filter((e) => e.entityType === "CLIMATE_ACTION").length,
    [selectedEntities],
  );

  const selectedProjectCount = useMemo(
    () => selectedEntities.filter((e) => e.entityType === "PROJECT").length,
    [selectedEntities],
  );

  const selectedIndicatorCount = useMemo(
    () => selectedEntities.filter((e) => e.entityType === "INDICATOR").length,
    [selectedEntities],
  );

  const handleClimateActionRowClick = useCallback(
    (row: Row<DatatableRowDataWithCacheKey<ClimateAction>>) => {
      if (!isRawRow(row.original)) return;
      toggleEntity(toSelectedEntity(row.original, "CLIMATE_ACTION"));
    },
    [toggleEntity],
  );

  const handleProjectRowClick = useCallback(
    (row: Row<DatatableRowDataWithCacheKey<Project>>) => {
      if (!isRawRow(row.original)) return;
      toggleEntity(toSelectedEntity(row.original, "PROJECT"));
    },
    [toggleEntity],
  );

  const handleIndicatorRowClick = useCallback(
    (row: Row<DatatableRowDataWithCacheKey<SelectableRow>>) => {
      if (!isRawRow(row.original)) return;
      toggleEntity(toSelectedEntity(row.original, "INDICATOR"));
    },
    [toggleEntity],
  );

  const handleIsRowSelected = useCallback(
    (row: Row<DatatableRowDataWithCacheKey<SelectableRow>>) => {
      if (!isRawRow(row.original)) return false;
      return selectedEntityIdSet.has(row.original.id);
    },
    [selectedEntityIdSet],
  );

  const selectionSummary = useMemo(() => {
    const partList: string[] = [];
    if (selectedClimateActionCount > 0) {
      partList.push(`${selectedClimateActionCount} ${terminology.climateAction.plural}`);
    }
    if (selectedProjectCount > 0) {
      partList.push(`${selectedProjectCount} ${t("create.tabProjects")}`);
    }
    if (selectedIndicatorCount > 0) {
      partList.push(`${selectedIndicatorCount} ${t("create.tabIndicators")}`);
    }
    return partList.length > 0 ? partList.join(", ") : t("create.noEntitiesSelected");
  }, [selectedClimateActionCount, selectedProjectCount, selectedIndicatorCount, terminology.climateAction.plural, t]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-end gap-3 border-b px-4 py-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="text-xs text-muted-foreground">{t("create.titleLabel")}</span>
          <Input
            id="data-request-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t("create.titlePlaceholder")}
            className="h-8"
          />
        </div>
        <div className="flex w-44 shrink-0 flex-col gap-1">
          <span className="text-xs text-muted-foreground">{t("create.dueDateLabel")}</span>
          <InputDate
            key={resetCount}
            onValueChange={(dateString) => setDueDate(dateString ? new Date(dateString) : null)}
            defaultValue={dueDate?.toISOString()}
            minDate={new Date()}
          />
        </div>
      </div>

      <div className="flex items-center justify-between border-b px-4 py-0">
        <Tabs value={entityMode} onValueChange={(value) => setEntityMode(value as EntityMode)}>
          <TabsList className="h-auto rounded-none bg-transparent p-0">
            <TabsTrigger
              value="CLIMATE_ACTION"
              className="relative rounded-none border-b-2 border-transparent px-3 py-2.5 text-sm text-muted-foreground shadow-none data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none"
            >
              {terminology.climateAction.plural}
            </TabsTrigger>
            <TabsTrigger
              value="PROJECT"
              className="relative rounded-none border-b-2 border-transparent px-3 py-2.5 text-sm text-muted-foreground shadow-none data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none"
            >
              {t("create.tabProjects")}
            </TabsTrigger>
            <TabsTrigger
              value="INDICATOR"
              className="relative rounded-none border-b-2 border-transparent px-3 py-2.5 text-sm text-muted-foreground shadow-none data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none"
            >
              {t("create.tabIndicators")}
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <span className="shrink-0 whitespace-nowrap text-xs text-muted-foreground">
          {selectionSummary}
        </span>
      </div>

      <div className="relative flex min-h-0 flex-1 items-stretch overflow-y-auto">
        {entityMode === "CLIMATE_ACTION" && (
          <Datatable<ClimateAction>
            viewKey={VIEW_KEY_CLIMATE_ACTION}
            workspaceId={currentWorkspace.id}
            data={safeClimateActionList}
            columns={climateActionColumns}
            initialState={{
              ...defaultDatatableState,
              showColumnHeaders: true,
              sectionColumnId: COLUMN_ID_CLIMATE_ACTION_FOCUS_AREA,
              sorting: [{ id: COLUMN_ID_TITLE, desc: false }],
            }}
            debugTable={false}
            synchToApi={false}
            initWithUrlParams={false}
            onRowClick={handleClimateActionRowClick}
            isRowSelected={handleIsRowSelected}
            config={{ quicksearch: true }}
            getColumnCanGlobalFilter={(column) => [COLUMN_ID_TITLE].includes(column.id)}
            globalFilterFn={filterByTitle}
          />
        )}
        {entityMode === "PROJECT" && (
          <Datatable<Project>
            viewKey={VIEW_KEY_PROJECT}
            workspaceId={currentWorkspace.id}
            data={safeProjectList}
            columns={projectColumns}
            initialState={{
              ...defaultDatatableState,
              showColumnHeaders: true,
              sectionColumnId: COLUMN_ID_PROJECT_FOCUS_AREA,
              sorting: [{ id: COLUMN_ID_TITLE, desc: false }],
            }}
            debugTable={false}
            synchToApi={false}
            initWithUrlParams={false}
            onRowClick={handleProjectRowClick}
            isRowSelected={handleIsRowSelected}
            config={{ quicksearch: true }}
            getColumnCanGlobalFilter={(column) => [COLUMN_ID_TITLE].includes(column.id)}
            globalFilterFn={filterByTitle}
          />
        )}
        {entityMode === "INDICATOR" && (
          <Datatable<SelectableRow>
            viewKey={VIEW_KEY_INDICATOR}
            workspaceId={currentWorkspace.id}
            data={safeIndicatorList}
            columns={indicatorColumns}
            initialState={{
              ...defaultDatatableState,
              showColumnHeaders: true,
              sorting: [{ id: COLUMN_ID_TITLE, desc: false }],
            }}
            debugTable={false}
            synchToApi={false}
            initWithUrlParams={false}
            onRowClick={handleIndicatorRowClick}
            isRowSelected={handleIsRowSelected}
            config={{ quicksearch: true }}
            getColumnCanGlobalFilter={(column) => [COLUMN_ID_TITLE].includes(column.id)}
            globalFilterFn={filterByTitle}
          />
        )}
      </div>
    </div>
  );
}
